/**
 * What the club has said, and what it did not manage to say.
 *
 * The screen is built around the suppressed rows, not the sent ones.
 * After a storm nobody at a club counts the forty texts that went; they
 * ask why eleven did not, and MSG-1 (0121) exists so every one of those
 * has a sentence beside it. So the default view is "not sent", the
 * reason is a column and not a tooltip, and the people are named rather
 * than counted.
 *
 * The second thing it does is tell the truth about reach BEFORE a storm.
 * 0112 prints a "reachable" number on the lightning screen, and that
 * number is worth nothing until somebody has seen the eleven it is
 * quietly excluding — so there is a dry run that names them.
 */

import { useEffect, useState } from 'react';

import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { dateTime } from '../lib/format.ts';

interface NotificationRow {
  notification_id: string;
  kind: string;
  class: string;
  topic: string;
  kind_description: string;
  person_id: string;
  person_name: string;
  channel: string;
  to_address: string | null;
  subject: string | null;
  body: string;
  status: string;
  reason: string | null;
  not_before: string;
  expires_at: string | null;
  attempts: number;
  last_error: string | null;
  sent_at: string | null;
  created_at: string;
}

interface ReachRow {
  kind: string;
  class: string;
  total: number;
  sent: number;
  queued: number;
  failed: number;
  suppressed: number;
  last_at: string | null;
}

interface Policy {
  quiet_from: string;
  quiet_to: string;
  sms_enabled: boolean;
  email_enabled: boolean;
  daily_cap_per_person: number | null;
  sender_name: string | null;
}

interface OutboxPayload {
  notifications: NotificationRow[];
  reach: ReachRow[];
  policy: Policy | null;
  delivering: boolean;
}

const CLASS_TONE: Record<string, 'critical' | 'warning' | 'neutral'> = {
  safety: 'critical',
  operational: 'neutral',
  commercial: 'warning',
};

/** 21:00:00 from Postgres; 21:00 on a screen. */
const hhmm = (t: string | null | undefined) => (t ?? '').slice(0, 5);

export function MessagesPage() {
  const [reload, setReload] = useState(0);
  const [notSent, setNotSent] = useState(true);
  const [kind, setKind] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const q = useApi<OutboxPayload>(
    `/v1/notifications/outbox?not_sent=${notSent}&days=14${
      kind ? `&kind=${encodeURIComponent(kind)}` : ''}`,
    [reload, notSent, kind],
  );

  const test = useMutation(async () => {
    const r = await api<{ notification: NotificationRow | null }>(
      '/v1/notifications/test', { method: 'POST', body: { channel: 'sms' } });
    setNote(r.notification?.status === 'queued'
      ? 'Queued. It is in the list below, and it will stay there until a carrier is set.'
      : `Not sent — ${r.notification?.reason ?? 'no reason given'}. That is the point of this screen.`);
    setReload((n) => n + 1);
  });

  const d = q.data;
  const rows = d?.notifications ?? [];

  const totals = (d?.reach ?? []).reduce(
    (t, r) => ({
      sent: t.sent + r.sent,
      queued: t.queued + r.queued,
      suppressed: t.suppressed + r.suppressed + r.failed,
    }),
    { sent: 0, queued: 0, suppressed: 0 },
  );

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Messages</h1>
          <div className="muted small">
            What the club has told its members, and — the part worth reading — what it
            could not.
          </div>
        </div>
      </div>

      {d && !d.delivering && (
        // The single most important sentence on the page. A queue that
        // never drains looks like a fault; it is a club that has not
        // chosen a carrier, and it should say so rather than be deduced.
        <div className="notice">
          <strong>No carrier is set up, so nothing is being delivered.</strong> Messages
          are still composed and queued, and everything below is what would have gone
          and to whom. Turn on a channel in the settings underneath once you have an
          account.
        </div>
      )}

      <div className="tiles">
        <Tile label="Sent" value={String(totals.sent)} />
        <Tile label="Waiting" value={String(totals.queued)} />
        <Tile label="Not sent" value={String(totals.suppressed)}
          note="the ones with a reason" />
      </div>

      <Card
        title={notSent ? 'What did not go out' : 'Everything'}
        actions={
          <div className="actions">
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Every kind</option>
              {(d?.reach ?? []).map((r) => (
                <option key={r.kind} value={r.kind}>{r.kind}</option>
              ))}
            </select>
            <button className="secondary small" onClick={() => setNotSent(!notSent)}>
              {notSent ? 'Show everything' : 'Only what did not go'}
            </button>
            <button className="secondary small" disabled={test.busy}
              onClick={() => test.run()}>
              Send myself a test
            </button>
          </div>
        }
      >
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? test.error} />

        {q.loading && rows.length === 0 ? <Loading /> : rows.length === 0 ? (
          <Empty>
            {notSent
              ? 'Nothing has been withheld in the last two weeks.'
              : 'Nothing has been composed in the last two weeks.'}
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>When</th><th>Who</th><th>What</th>
                <th>State</th><th>Why not</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.notification_id}>
                  <td className="small">
                    {dateTime(r.created_at)}
                    {r.expires_at && (
                      <div className="hint">expires {dateTime(r.expires_at)}</div>
                    )}
                  </td>
                  <td>
                    {r.person_name}
                    <div className="hint mono">{r.to_address ?? '—'}</div>
                  </td>
                  <td className="small">
                    {r.kind}
                    <div className="hint">{r.body}</div>
                  </td>
                  <td>
                    {r.status === 'sent' ? <Status tone="good">Sent</Status>
                      : r.status === 'queued' ? <Status tone="neutral">Waiting</Status>
                        : r.status === 'failed' ? <Status tone="critical">Failed</Status>
                          : <Status tone="warning">Not sent</Status>}
                    {r.class === 'safety' && <div className="hint">safety</div>}
                  </td>
                  <td className="small">
                    {/* The sentence the whole screen exists for. */}
                    {r.reason ?? r.last_error ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ReachCheck />
      {/* Only once the club's own settings have arrived. Rendered
          earlier it shows the defaults, and a form that quietly says
          "text messages: off" to a club that turned them on is a form
          somebody will save. */}
      {d && (
        <NotificationPolicy policy={d.policy} onSaved={() => setReload((n) => n + 1)} />
      )}
      <Kinds />
    </>
  );
}


/* ================================================================== */
/* Who could be told, if it happened now                               */
/* ================================================================== */

interface ReachPerson {
  person_id: string;
  person_name: string;
  has_number: boolean;
  why_not: string | null;
}

/**
 * The dry run.
 *
 * 0112 already shows a "reachable" count on the lightning screen. This
 * is the same question asked honestly: not how many, but which ones —
 * and for the ones who could not be reached, the sentence somebody would
 * need in order to fix it before the next storm.
 */
function ReachCheck() {
  const [open, setOpen] = useState(false);
  const q = useApi<{
    on_the_course: ReachPerson[]; reachable: number; unreachable: ReachPerson[];
  }>(open ? '/v1/notifications/reach-check' : null, [open]);

  return (
    <Card
      title="If the Course Were Shut Right Now"
      actions={
        <button className="secondary small" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Check'}
        </button>
      }
    >
      <p className="muted small">
        Everybody who is checked in or keeping a card, and whether the club could
        actually reach them. A &ldquo;reachable&rdquo; number is worth nothing until
        somebody has seen the people it is leaving out.
      </p>

      {open && (q.loading ? <Loading /> : !q.data ? <ErrorBanner error={q.error} /> : (
        q.data.on_the_course.length === 0 ? (
          <Empty>Nobody is out. Nothing to check.</Empty>
        ) : (
          <>
            <p>
              <strong>{q.data.reachable}</strong> of {q.data.on_the_course.length} could
              be reached.
            </p>
            {q.data.unreachable.length > 0 && (
              <table className="data">
                <thead><tr><th>Who</th><th>Why not</th></tr></thead>
                <tbody>
                  {q.data.unreachable.map((p) => (
                    <tr key={p.person_id}>
                      <td>{p.person_name}</td>
                      <td className="small">{p.why_not}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )
      ))}
    </Card>
  );
}


/* ================================================================== */
/* The settings                                                        */
/* ================================================================== */

function NotificationPolicy({
  policy, onSaved,
}: { policy: Policy | null; onSaved: () => void }) {
  const [f, setF] = useState({
    quiet_from: hhmm(policy?.quiet_from) || '21:00',
    quiet_to: hhmm(policy?.quiet_to) || '07:30',
    sms_enabled: policy?.sms_enabled ?? false,
    email_enabled: policy?.email_enabled ?? false,
    daily_cap_per_person: policy?.daily_cap_per_person
      ? String(policy.daily_cap_per_person) : '',
    sender_name: policy?.sender_name ?? '',
  });
  const [note, setNote] = useState<string | null>(null);

  // And re-synced if it changes underneath — somebody else saving from
  // another screen, or the reload after this form's own save. useState's
  // initial value is read once and only once, which is exactly the trap
  // the guard above is protecting against and this closes the other half
  // of.
  useEffect(() => {
    if (!policy) return;
    setF({
      quiet_from: hhmm(policy.quiet_from) || '21:00',
      quiet_to: hhmm(policy.quiet_to) || '07:30',
      sms_enabled: policy.sms_enabled,
      email_enabled: policy.email_enabled,
      daily_cap_per_person: policy.daily_cap_per_person
        ? String(policy.daily_cap_per_person) : '',
      sender_name: policy.sender_name ?? '',
    });
  }, [
    policy?.quiet_from, policy?.quiet_to, policy?.sms_enabled,
    policy?.email_enabled, policy?.daily_cap_per_person, policy?.sender_name,
  ]);

  const save = useMutation(async () => {
    await api('/v1/notifications/policy', {
      method: 'PUT',
      body: {
        quiet_from: f.quiet_from,
        quiet_to: f.quiet_to,
        sms_enabled: f.sms_enabled,
        email_enabled: f.email_enabled,
        daily_cap_per_person: f.daily_cap_per_person
          ? Number(f.daily_cap_per_person) : null,
        sender_name: f.sender_name.trim() || null,
      },
    });
    setNote('Saved.');
    onSaved();
  });

  return (
    <Card title="How the Club Sends">
      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={save.error} />

      <div className="grid-2">
        <label className="field">
          <span>Quiet from</span>
          <input type="time" value={f.quiet_from}
            onChange={(e) => setF({ ...f, quiet_from: e.target.value })} />
          <div className="hint">
            The club&apos;s own time, not the server&apos;s. A message queued inside
            these hours waits for the morning.
          </div>
        </label>
        <label className="field">
          <span>Until</span>
          <input type="time" value={f.quiet_to}
            onChange={(e) => setF({ ...f, quiet_to: e.target.value })} />
          <div className="hint">
            {/* The exemption, stated where somebody will read it rather
                than discovered at half past ten at night. */}
            A safety message — lightning, the course closing — ignores this. Nothing
            else can.
          </div>
        </label>
        <label className="field">
          <span>Most messages one member gets in a day</span>
          <input type="number" min={1} max={50} value={f.daily_cap_per_person}
            placeholder="no limit"
            onChange={(e) => setF({ ...f, daily_cap_per_person: e.target.value })} />
          <div className="hint">
            A club that sends somebody eleven things on a Saturday loses them for
            everything, including the one that mattered. Safety messages do not count
            against this.
          </div>
        </label>
        <label className="field">
          <span>What a text says it is from</span>
          <input value={f.sender_name} placeholder="the club's brand name"
            maxLength={40}
            onChange={(e) => setF({ ...f, sender_name: e.target.value })} />
        </label>
      </div>

      <div className="row" style={{ gap: 20, marginTop: 8 }}>
        <label className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 0 }}>
          <input type="checkbox" style={{ width: 'auto', flex: '0 0 auto' }}
            checked={f.sms_enabled}
            onChange={(e) => setF({ ...f, sms_enabled: e.target.checked })} />
          <span className="small">Text messages</span>
        </label>
        <label className="row" style={{ gap: 6, alignItems: 'center', marginBottom: 0 }}>
          <input type="checkbox" style={{ width: 'auto', flex: '0 0 auto' }}
            checked={f.email_enabled}
            onChange={(e) => setF({ ...f, email_enabled: e.target.checked })} />
          <span className="small">Email</span>
        </label>
      </div>
      <p className="hint">
        These say what the club <em>intends</em> to use. Nothing is delivered until a
        carrier is connected — until then everything queues and shows up above.
      </p>

      <div className="actions">
        <button className="gold" disabled={save.busy} onClick={() => save.run()}>
          Save
        </button>
      </div>
    </Card>
  );
}


/* ================================================================== */
/* What the software knows how to say                                  */
/* ================================================================== */

interface KindRow {
  kind: string;
  class: string;
  topic: string;
  default_channel: string;
  expires_after_minutes: number | null;
  ignores_quiet_hours: boolean;
  ignores_consent: boolean;
  description: string;
}

/**
 * On the screen because it is the thing a club will argue with.
 *
 * "Why does the lightning one ignore my quiet hours" deserves an answer
 * a manager can read, in the application, rather than one that lives in
 * a migration comment.
 */
function Kinds() {
  const [open, setOpen] = useState(false);
  const q = useApi<{ kinds: KindRow[] }>(open ? '/v1/notifications/kinds' : null, [open]);

  return (
    <Card
      title="What the Club Can Send, and the Rules Each One Follows"
      actions={
        <button className="secondary small" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show'}
        </button>
      }
    >
      {open && (q.loading ? <Loading /> : (
        <table className="data">
          <thead>
            <tr>
              <th>Kind</th><th>Class</th><th>Consent</th>
              <th>Quiet hours</th><th>Life</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.kinds ?? []).map((k) => (
              <tr key={k.kind}>
                <td>
                  <span className="mono small">{k.kind}</span>
                  <div className="hint">{k.description}</div>
                </td>
                <td><Status tone={CLASS_TONE[k.class] ?? 'neutral'}>{k.class}</Status></td>
                <td className="small">
                  {k.ignores_consent
                    ? <span className="muted">sent regardless</span>
                    : k.topic}
                </td>
                <td className="small">
                  {k.ignores_quiet_hours
                    ? <span className="muted">ignored</span>
                    : 'held until morning'}
                </td>
                <td className="small">
                  {k.expires_after_minutes
                    ? `${k.expires_after_minutes} min`
                    : <span className="muted">never stale</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </Card>
  );
}
